import React, { useState } from 'react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import appData from '../../data/data.json';
import engineerSignature from '../../assets/gas_engineer_sign.png';
import { images } from '../../const/assets';
import './GasSafetyForm.css';
import SignaturePad from './SignaturePad';

/* ---------- small reusable select/radio helpers ---------- */

const YNA = ({ value, onChange }) => (
    <select className="cell-select" value={value} onChange={onChange}>
        <option value=""></option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
        <option value="N/A">N/A</option>
    </select>
);

const PassFail = ({ value, onChange }) => (
    <select className="cell-select" value={value} onChange={onChange}>
        <option value=""></option>
        <option value="Pass">Pass</option>
        <option value="Fail">Fail</option>
        <option value="N/A">N/A</option>
    </select>
);

// Yes / No radio pair (for pipework row)
const RadioYN = ({ name, value, onChange }) => (
    <span className="radio-group">
        <label><input type="radio" name={name} value="Yes" checked={value === 'Yes'} onChange={onChange} /> Yes</label>
        <label><input type="radio" name={name} value="No"  checked={value === 'No'}  onChange={onChange} /> No</label>
    </span>
);

// Yes / No / N/A radio triple (for CO alarms)
const RadioYNA = ({ name, value, onChange }) => (
    <span className="radio-group">
        <label><input type="radio" name={name} value="Yes" checked={value === 'Yes'} onChange={onChange} /> Yes</label>
        <label><input type="radio" name={name} value="No"  checked={value === 'No'}  onChange={onChange} /> No</label>
        <label><input type="radio" name={name} value="N/A" checked={value === 'N/A'} onChange={onChange} /> N/A</label>
    </span>
);

/* ---------- state factories ---------- */

const emptyAppliance = () => ({
    location: '',
    makeModel: '',
    type: '',
    flueType: '',           // OF / Rs / FL
    opPressure: '',         // mbar or kWh/Btu/h
    safetyDevice: '',       // Yes/No/NA
    spillage: '',           // Pass/Fail/NA
    smokePellet: '',        // Pass/Fail/NA
    initialCombustion: '',  // free text reading
    finalCombustion: '',    // free text reading
    satTermination: '',     // Yes/No/NA
    flueVisual: '',         // Pass/Fail/NA
    adequateVent: '',       // Yes/No/NA
    landlordsApp: '',       // Yes/No/NA
    inspected: '',          // Yes/No
    visualCheck: '',        // Yes/No
    serviced: '',           // Yes/No
    safeToUse: '',          // Yes/No
});

const emptyFault = () => ({
    faultDetails: '',
    rectification: '',
    warningNotice: '',      // Yes/No/NA
    warningTag: '',         // Yes/No/NA
});

const initialState = {
    reportRef: 'BDC0000001',
    // Inspection address
    insp_nameTitle: '',
    insp_address: '',
    insp_postcode: '',
    insp_tel: '',
    insp_rentedYes: false,
    insp_rentedNo: false,
    // Landlord address
    land_nameTitle: '',
    land_address: '',
    land_postcode: '',
    land_tel: '',
    // Work description
    descriptionOfWork: '',
    // Appliance table
    appliances: Array.from({ length: 5 }, emptyAppliance),
    // Gas pipework (Yes/No radio)
    pw_visual: '',
    pw_emergency: '',
    pw_tightness: '',
    pw_bonding: '',
    // Faults table
    faults: Array.from({ length: 5 }, emptyFault),
    // CO alarms (Yes/No/NA radio)
    co_fitted: '',
    co_inDate: '',
    co_testing: '',
    co_smoke: '',
    // Summary
    totalAppliances: '',
    nextCheckDue: '',
    // Issued by (engineer pre-filled)
    eng_signed: engineerSignature,
    eng_issueDate: '',
    // Received by
    rec_by: '',
    rec_signed: '',
    rec_printName: '',
    rec_role: '',
    rec_nonePresent: false,
};

/* ---------- main component ---------- */

const GasSafetyForm = () => {
    const [form, setForm] = useState(initialState);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const setApp = (i, field, value) =>
        setForm(prev => ({
            ...prev,
            appliances: prev.appliances.map((a, idx) => idx === i ? { ...a, [field]: value } : a),
        }));

    const setFlt = (i, field, value) =>
        setForm(prev => ({
            ...prev,
            faults: prev.faults.map((f, idx) => idx === i ? { ...f, [field]: value } : f),
        }));

    const prepareClone = (clonedDoc) => {
        // Radio buttons: remove unchecked labels; replace the whole checked label with its value
        clonedDoc.querySelectorAll('input[type="radio"]').forEach(el => {
            const label = el.closest('label');
            if (!el.checked) {
                if (label) label.remove();
                else el.remove();
            } else {
                const span = clonedDoc.createElement('span');
                span.textContent = el.value;
                span.style.fontWeight = 'bold';
                if (label) label.parentNode.replaceChild(span, label);
                else el.parentNode.replaceChild(span, el);
            }
        });

        // Hide labels marked as pdf-only-if-checked when their checkbox is unchecked
        clonedDoc.querySelectorAll('[data-hide-if-unchecked]').forEach(el => {
            const cb = el.querySelector('input[type="checkbox"]');
            if (cb && !cb.checked) el.style.display = 'none';
        });

        // Checkboxes: replace with ✓ or empty
        clonedDoc.querySelectorAll('input[type="checkbox"]').forEach(el => {
            const span = clonedDoc.createElement('span');
            span.textContent = el.checked ? '✓' : '';
            el.parentNode.replaceChild(span, el);
        });

        // Text / date / textarea: replace with div showing value
        clonedDoc.querySelectorAll('input:not([type="radio"]):not([type="checkbox"]), textarea').forEach(el => {
            const div = clonedDoc.createElement('div');
            const isDate = el.type === 'date' && /^\d{4}-\d{2}-\d{2}$/.test(el.value);
            div.textContent = isDate ? el.value.split('-').reverse().join('-') : el.value;
            div.className = el.className;
            if (el.getAttribute('style')) div.setAttribute('style', el.getAttribute('style'));
            div.style.display = 'flex';
            div.style.alignItems = 'center';
            el.parentNode.replaceChild(div, el);
        });

        // Selects: replace with div showing selected option text
        clonedDoc.querySelectorAll('select').forEach(el => {
            const div = clonedDoc.createElement('div');
            div.textContent = el.options[el.selectedIndex]?.text || '';
            div.className = el.className;
            div.style.display = 'flex';
            div.style.alignItems = 'center';
            el.parentNode.replaceChild(div, el);
        });
    };

    const handleDownload = async () => {
        const element = document.getElementById('gas-safety-form');
        const noPrintEls = element.querySelectorAll('.no-print');
        noPrintEls.forEach(el => (el.style.display = 'none'));

        try {
            // Render the entire form to a single canvas at 2× resolution
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                scrollY: 0,
                windowWidth: element.scrollWidth,
                onclone: (clonedDoc) => prepareClone(clonedDoc),
            });

            // A4 landscape usable area (mm) with 4 mm margins
            const MARGIN = 4;
            const PAGE_W = 297 - MARGIN * 2;   // 289 mm
            const PAGE_H = 210 - MARGIN * 2;   // 202 mm

            // Convert canvas pixels → mm (96 dpi, scale=2 so divide by 2 first)
            const PX_PER_MM = 96 / 25.4;
            const naturalW = canvas.width  / 2 / PX_PER_MM;
            const naturalH = canvas.height / 2 / PX_PER_MM;

            // Uniform scale-to-fit: never enlarge, always shrink if needed
            const fit = Math.min(PAGE_W / naturalW, PAGE_H / naturalH, 1);
            const imgW = naturalW * fit;
            const imgH = naturalH * fit;

            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
            pdf.addImage(canvas.toDataURL('image/png'), 'PNG', MARGIN, MARGIN, imgW, imgH);
            const reportRef = (form.reportRef || 'unknown-ref')
                .replace(/[^a-zA-Z0-9]/g, '_')
                .slice(0, 60);
            const address  = (form.insp_address || 'unknown-address')
                .replace(/[^a-zA-Z0-9 ]/g, '')   // remove commas, dots and all non-alphanumeric
                .trim()
                .replace(/\s+/g, '_')             // spaces → underscores
                .slice(0, 60);                    // cap length
            pdf.save(`${address}_${reportRef}.pdf`);
        } finally {
            noPrintEls.forEach(el => (el.style.display = ''));
        }
    };

    /* ---------- render ---------- */
    return (
        <div className="gsr-outer">
            {/* toolbar */}
            <div className="no-print gsr-toolbar">
                <h5 className="m-0">Gas Safety Record (REGP46)</h5>
                <button className="btn btn-dark btn-sm" onClick={handleDownload}>Download PDF</button>
            </div>

            <div id="gas-safety-form" className="gas-form">

                {/* ── Header row ── */}
                <table className="gf-table mb-1">
                    <tbody>
                        <tr>
                            <td style={{ width: '60%', background: '#1b3a5c', padding: '6px 8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <img src={images.Logo} alt="logo" style={{ height: '40px', flexShrink: 0 }} />
                                    <div>
                                        <strong style={{ color: '#fff', fontSize: '1.04rem', letterSpacing: '0.05em' }}>GAS SAFETY RECORD</strong>
                                        <div className="disclaimer" style={{ color: '#c8d8ec' }}>
                                            This inspection is for gas safety purposes only to comply with the Gas Safety (Installation and Use) Regulations.
                                            Flues have been inspected visually and checked for satisfactory evacuation of products of combustion.
                                            A detailed internal inspection of the flue integrity, construction and lining has NOT been carried out.
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td style={{ width: '40%', background: '#eef4fb' }}>
                                <div className="inline-field">
                                    <strong>Report Ref No:</strong>
                                    <input className="gf-input flex-1" value={form.reportRef} onChange={e => set('reportRef', e.target.value)} />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Three-column address row ── */}
                <table className="gf-table mb-1">
                    <tbody>
                        <tr>
                            {/* Business details – read-only */}
                            <td className="addr-cell" style={{ width: '33%', background: '#eef4fb' }}>
                                <div className="section-hd">REGISTERED BUSINESS DETAILS</div>
                                <div><span className="lbl">Reg No:</span> {appData.business.regNo}</div>
                                <div><span className="lbl">Company:</span> {appData.business.company}</div>
                                <div><span className="lbl">Address:</span> {appData.business.address}</div>
                                <div><span className="lbl">Postcode:</span> {appData.business.postcode}</div>
                                <div><span className="lbl">Tel:</span> {appData.business.tel}</div>
                            </td>

                            {/* Inspection address – editable */}
                            <td className="addr-cell" style={{ width: '33%' }}>
                                <div className="section-hd">INSPECTION/INSTALLATION ADDRESS</div>
                                <div className="inline-field mb-1">
                                    <span className="lbl">Name &amp; Title:</span>
                                    <input className="gf-input flex-1" value={form.insp_nameTitle} onChange={e => set('insp_nameTitle', e.target.value)} />
                                </div>
                                <div className="inline-field mb-1">
                                    <span className="lbl">Address:</span>
                                    <input className="gf-input flex-1" value={form.insp_address} onChange={e => set('insp_address', e.target.value)} />
                                </div>
                                <div className="inline-field mb-1">
                                    <span className="lbl">Postcode:</span>
                                    <input className="gf-input" style={{ width: '120px' }} value={form.insp_postcode} onChange={e => set('insp_postcode', e.target.value)} />
                                    <span className="lbl ml-8">Tel:</span>
                                    <input className="gf-input flex-1" value={form.insp_tel} onChange={e => set('insp_tel', e.target.value)} />
                                </div>
                                <div className="inline-field">
                                    <span className="lbl">Rented:</span>
                                    <label className="chk-lbl ml-4"><input type="checkbox" checked={form.insp_rentedYes} onChange={e => set('insp_rentedYes', e.target.checked)} /> Yes</label>
                                    <label className="chk-lbl ml-4"><input type="checkbox" checked={form.insp_rentedNo}  onChange={e => set('insp_rentedNo',  e.target.checked)} /> No</label>
                                </div>
                            </td>

                            {/* Landlord address – editable */}
                            <td className="addr-cell" style={{ width: '34%' }}>
                                <div className="section-hd">LANDLORD (OR AGENT) NAME &amp; ADDRESS <span style={{ fontWeight: 'normal' }}>(if applicable)</span></div>
                                <div className="inline-field mb-1">
                                    <span className="lbl">Name &amp; Title:</span>
                                    <input className="gf-input flex-1" value={form.land_nameTitle} onChange={e => set('land_nameTitle', e.target.value)} />
                                </div>
                                <div className="inline-field mb-1">
                                    <span className="lbl">Address:</span>
                                    <input className="gf-input flex-1" value={form.land_address} onChange={e => set('land_address', e.target.value)} />
                                </div>
                                <div className="inline-field">
                                    <span className="lbl">Postcode:</span>
                                    <input className="gf-input" style={{ width: '120px' }} value={form.land_postcode} onChange={e => set('land_postcode', e.target.value)} />
                                    <span className="lbl ml-8">Tel:</span>
                                    <input className="gf-input flex-1" value={form.land_tel} onChange={e => set('land_tel', e.target.value)} />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Description of work ── */}
                <table className="gf-table mb-1">
                    <tbody>
                        <tr>
                            <td>
                                <div className="section-hd text-center">DESCRIPTION OF WORK CARRIED OUT</div>
                                <textarea className="gf-input w-full" rows={2} value={form.descriptionOfWork} onChange={e => set('descriptionOfWork', e.target.value)} style={{ resize: 'none' }} />
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Appliance Details Table (3-group header) ── */}
                <table className="gf-table appliance-table mb-1">
                    <thead>
                        {/* Group header row */}
                        <tr>
                            <th colSpan={5} className="group-hd">APPLIANCE DETAILS</th>
                            <th colSpan={6} className="group-hd">FLUE TESTS</th>
                            <th colSpan={8} className="group-hd">INSPECTION DETAILS</th>
                        </tr>
                        {/* Column header row */}
                        <tr className="col-hd-row">
                            <th className="col-hd" style={{ width: '2%' }}>#</th>
                            <th className="col-hd" style={{ width: '8%' }}>Location</th>
                            <th className="col-hd" style={{ width: '10%' }}>Make and Model</th>
                            <th className="col-hd" style={{ width: '5%' }}>Type</th>
                            <th className="col-hd" style={{ width: '5%' }}>Flue Type OF/Rs/FL</th>
                            {/* FLUE TESTS */}
                            <th className="col-hd" style={{ width: '6%' }}>Operating pressure in mbar or heat input kWh or Btu/h</th>
                            <th className="col-hd" style={{ width: '5%' }}>Safety device(s) correct operation Yes/No/NA</th>
                            <th className="col-hd" style={{ width: '5%' }}>Spillage test Pass/Fail/NA</th>
                            <th className="col-hd" style={{ width: '5%' }}>Smoke pellet flue flow test Pass/Fail/NA</th>
                            <th className="col-hd" style={{ width: '6%' }}>Initial combustion analyser reading</th>
                            <th className="col-hd" style={{ width: '6%' }}>Final combustion analyser reading</th>
                            {/* INSPECTION DETAILS */}
                            <th className="col-hd" style={{ width: '5%' }}>Satisfactory termination Yes/No/NA</th>
                            <th className="col-hd" style={{ width: '5%' }}>Flue visual condition Pass/Fail/NA</th>
                            <th className="col-hd" style={{ width: '5%' }}>Adequate ventilation Yes/No</th>
                            <th className="col-hd" style={{ width: '5%' }}>Landlord's appliance Yes/No/NA</th>
                            <th className="col-hd" style={{ width: '4%' }}>Inspected Yes/No</th>
                            <th className="col-hd" style={{ width: '5%' }}>Appliance Visual Check Yes/No</th>
                            <th className="col-hd" style={{ width: '5%' }}>Appliance serviced Yes/No</th>
                            <th className="col-hd" style={{ width: '5%' }}>Appliance Safe to Use Yes/No</th>
                        </tr>
                    </thead>
                    <tbody>
                        {form.appliances.map((a, i) => (
                            <tr key={i}>
                                <td className="text-center">{i + 1}</td>
                                <td><input className="cell-input" value={a.location}        onChange={e => setApp(i, 'location',        e.target.value)} /></td>
                                <td><input className="cell-input" value={a.makeModel}       onChange={e => setApp(i, 'makeModel',       e.target.value)} /></td>
                                <td><select className="cell-select" value={a.type} onChange={e => setApp(i, 'type', e.target.value)}><option value=""></option><option value="Boiler">Boiler</option><option value="Cooker">Cooker</option><option value="Hob">Hob</option></select></td>
                                <td><select className="cell-select" value={a.flueType} onChange={e => setApp(i, 'flueType', e.target.value)}><option value=""></option><option value="OF">OF</option><option value="Rs">Rs</option><option value="FL">FL</option></select></td>
                                <td><input className="cell-input" value={a.opPressure}      onChange={e => setApp(i, 'opPressure',      e.target.value)} /></td>
                                <td><YNA       value={a.safetyDevice}    onChange={e => setApp(i, 'safetyDevice',    e.target.value)} /></td>
                                <td><PassFail  value={a.spillage}        onChange={e => setApp(i, 'spillage',        e.target.value)} /></td>
                                <td><PassFail  value={a.smokePellet}     onChange={e => setApp(i, 'smokePellet',     e.target.value)} /></td>
                                <td><input className="cell-input" value={a.initialCombustion} onChange={e => setApp(i, 'initialCombustion', e.target.value)} /></td>
                                <td><input className="cell-input" value={a.finalCombustion}   onChange={e => setApp(i, 'finalCombustion',   e.target.value)} /></td>
                                <td><YNA       value={a.satTermination}  onChange={e => setApp(i, 'satTermination',  e.target.value)} /></td>
                                <td><PassFail  value={a.flueVisual}      onChange={e => setApp(i, 'flueVisual',      e.target.value)} /></td>
                                <td><YNA       value={a.adequateVent}    onChange={e => setApp(i, 'adequateVent',    e.target.value)} /></td>
                                <td><YNA       value={a.landlordsApp}    onChange={e => setApp(i, 'landlordsApp',    e.target.value)} /></td>
                                <td><YNA       value={a.inspected}       onChange={e => setApp(i, 'inspected',       e.target.value)} /></td>
                                <td><YNA       value={a.visualCheck}     onChange={e => setApp(i, 'visualCheck',     e.target.value)} /></td>
                                <td><YNA       value={a.serviced}        onChange={e => setApp(i, 'serviced',        e.target.value)} /></td>
                                <td><YNA       value={a.safeToUse}       onChange={e => setApp(i, 'safeToUse',       e.target.value)} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* italics note below appliance table */}
                <div className="note mb-1">
                    <em>For appliances not owned by the landlord the recorded 'Appliance Safe to Use' response is based on a visual check for obvious defects only</em>
                </div>

                {/* ── Gas Installation Pipework ── */}
                <table className="gf-table mb-1">
                    <tbody>
                        <tr>
                            <td style={{ width: '13%' }}><strong>Gas Installation Pipework:</strong></td>
                            <td style={{ width: '22%' }}>
                                <div className="lbl">Satisfactory Visual Inspection:</div>
                                <RadioYN name="pw_visual" value={form.pw_visual} onChange={e => set('pw_visual', e.target.value)} />
                            </td>
                            <td style={{ width: '22%' }}>
                                <div className="lbl">Emergency Control Accessible:</div>
                                <RadioYN name="pw_emergency" value={form.pw_emergency} onChange={e => set('pw_emergency', e.target.value)} />
                            </td>
                            <td style={{ width: '22%' }}>
                                <div className="lbl">Satisfactory Gas Tightness Test:</div>
                                <RadioYN name="pw_tightness" value={form.pw_tightness} onChange={e => set('pw_tightness', e.target.value)} />
                            </td>
                            <td style={{ width: '21%' }}>
                                <div className="lbl">Equipotential Bonding Satisfactory:</div>
                                <RadioYN name="pw_bonding" value={form.pw_bonding} onChange={e => set('pw_bonding', e.target.value)} />
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Faults / Rectification ── */}
                <table className="gf-table mb-1">
                    <thead>
                        <tr>
                            <th className="group-hd" style={{ width: '40%' }}>GIVE DETAILS OF ANY FAULTS</th>
                            <th className="group-hd" style={{ width: '42%' }}>RECTIFICATION WORK CARRIED OUT</th>
                            <th className="group-hd" style={{ width: '9%', fontSize: '0.6rem' }}>WARNING NOTICE ISSUED Yes/No/NA *</th>
                            <th className="group-hd" style={{ width: '9%', fontSize: '0.6rem' }}>WARNING TAG or LABEL FIXED Yes/No/NA</th>
                        </tr>
                    </thead>
                    <tbody>
                        {form.faults.map((f, i) => (
                            <tr key={i}>
                                <td><input className="cell-input" value={f.faultDetails}  onChange={e => setFlt(i, 'faultDetails',  e.target.value)} /></td>
                                <td><input className="cell-input" value={f.rectification} onChange={e => setFlt(i, 'rectification', e.target.value)} /></td>
                                <td><YNA value={f.warningNotice} onChange={e => setFlt(i, 'warningNotice', e.target.value)} /></td>
                                <td><YNA value={f.warningTag}    onChange={e => setFlt(i, 'warningTag',    e.target.value)} /></td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                {/* ── CO Alarms ── */}
                <table className="gf-table mb-1">
                    <tbody>
                        <tr>
                            <td style={{ width: '25%' }}>
                                <div className="lbl">Approved Audible CO Alarms Fitted &amp; Located Correctly**:</div>
                                <RadioYNA name="co_fitted" value={form.co_fitted} onChange={e => set('co_fitted', e.target.value)} />
                            </td>
                            <td style={{ width: '25%' }}>
                                <div className="lbl">Are CO Alarms in Date:</div>
                                <RadioYNA name="co_inDate" value={form.co_inDate} onChange={e => set('co_inDate', e.target.value)} />
                            </td>
                            <td style={{ width: '25%' }}>
                                <div className="lbl">Testing of CO Alarms Satisfactory:</div>
                                <RadioYNA name="co_testing" value={form.co_testing} onChange={e => set('co_testing', e.target.value)} />
                            </td>
                            <td style={{ width: '25%' }}>
                                <div className="lbl">Smoke/Heat Alarms Located &amp; Fitted correctly**:</div>
                                <RadioYNA name="co_smoke" value={form.co_smoke} onChange={e => set('co_smoke', e.target.value)} />
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Summary ── */}
                <table className="gf-table mb-1">
                    <tbody>
                        <tr>
                            <td style={{ width: '35%' }}>
                                <div className="inline-field">
                                    <span className="lbl">Number of appliances tested:</span>
                                    <input className="gf-input" style={{ width: '45px' }} value={form.totalAppliances} onChange={e => set('totalAppliances', e.target.value)} />
                                </div>
                            </td>
                            <td style={{ width: '65%' }}>
                                <div className="inline-field">
                                    <strong>NEXT GAS SAFETY CHECK DUE BEFORE:</strong>
                                    <input type="date" className="gf-input ml-8" value={form.nextCheckDue} onChange={e => set('nextCheckDue', e.target.value)} />
                                </div>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Issued By / Received By ── */}
                <table className="gf-table mb-1">
                    <thead>
                        <tr>
                            <th colSpan={2} className="group-hd" style={{ width: '50%' }}>ISSUED BY (GAS ENGINEER)</th>
                            <th colSpan={2} className="group-hd" style={{ width: '50%' }}>RECEIVED BY</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {/* Issued By */}
                            <td style={{ width: '25%', verticalAlign: 'top' }}>
                                <div className="lbl">Print Name:</div>
                                <div>{appData.engineer.name}</div>
                                <div className="lbl mt-4">Licence No:</div>
                                <div>{appData.engineer.licenceNo}</div>
                            </td>
                            <td style={{ width: '25%', verticalAlign: 'top' }}>
                                <div className="lbl">Signed:</div>
                                <SignaturePad
                                    label="Gas Engineer Signature"
                                    value={form.eng_signed}
                                    onChange={val => set('eng_signed', val)}
                                />
                                <div className="lbl mt-4">Issue Date:</div>
                                <input type="date" className="gf-input w-full" value={form.eng_issueDate} onChange={e => set('eng_issueDate', e.target.value)} />
                            </td>
                            {/* Received By */}
                            <td style={{ width: '25%', verticalAlign: 'top' }}>
                                <div className="lbl">Received By:</div>
                                <input className="gf-input w-full mb-1" value={form.rec_by} onChange={e => set('rec_by', e.target.value)} />
                                <div className="lbl">Signed:</div>
                                <SignaturePad
                                    label="Recipient Signature"
                                    value={form.rec_signed}
                                    onChange={val => set('rec_signed', val)}
                                />
                                <div className="lbl">Print Name:</div>
                                <input className="gf-input w-full" value={form.rec_printName} onChange={e => set('rec_printName', e.target.value)} />
                            </td>
                            <td style={{ width: '25%', verticalAlign: 'top' }}>
                                <div style={{ fontSize: '0.65rem', marginBottom: '3px' }}>(Delete as applicable)</div>
                                <div className="lbl">Tenant / Agent / Landlord / Home Owner</div>
                                <select className="gf-input w-full mt-1" value={form.rec_role} onChange={e => set('rec_role', e.target.value)}>
                                    <option value=""></option>
                                    <option value="Tenant">Tenant</option>
                                    <option value="Agent">Agent</option>
                                    <option value="Landlord">Landlord</option>
                                    <option value="Home Owner">Home Owner</option>
                                </select>
                                <label className="chk-lbl d-flex mt-2" style={{ gap: '5px', fontWeight: 'normal' }} data-hide-if-unchecked>
                                    <input type="checkbox" checked={form.rec_nonePresent} onChange={e => set('rec_nonePresent', e.target.checked)} />
                                    No one present at time of visit
                                </label>
                            </td>
                        </tr>
                    </tbody>
                </table>

            </div>

            {/* bottom toolbar */}
            <div className="no-print gsr-toolbar mt-3">
                <span />
                <button className="btn btn-dark btn-sm" onClick={handleDownload}>Download PDF</button>
            </div>
        </div>
    );
};

export default GasSafetyForm;
